// ── 10 Pillar definitions with weights (rebalanced to sum to 1.0) ──
export const PILLARS = [
  { id: 'concept_clarity',        name: 'Concept Clarity',            weight: 0.12, description: 'Is the concept clearly defined, differentiated, and ownable?' },
  { id: 'brand_expression',       name: 'Brand Expression',           weight: 0.10, description: 'Visual identity, tone, naming, and consistency across touchpoints.' },
  { id: 'guest_signal',           name: 'Guest Signal',               weight: 0.10, description: 'Review sentiment, guest feedback patterns, and loyalty signals.' },
  { id: 'operational_confidence', name: 'Operational Confidence',     weight: 0.10, description: 'Staffing, systems, consistency, and operational readiness.' },
  { id: 'demand_generation',      name: 'Demand Generation',          weight: 0.08, description: 'Marketing, PR, social presence, and awareness-building.' },
  { id: 'commercial_performance', name: 'Commercial Performance',     weight: 0.12, description: 'Revenue, profitability, check averages, and financial health.' },
  { id: 'growth_readiness',       name: 'Growth Readiness',           weight: 0.10, description: 'Scalability, systems, leadership, and expansion potential.' },
  { id: 'emotional_experience',   name: 'Emotional Experience',       weight: 0.10, description: 'Sensory layers — sound, lighting, scent, texture, pacing, feeling.' },
  { id: 'menu_intelligence',      name: 'Menu & Ingredient Intel',    weight: 0.09, description: 'Trends, engineering, pricing logic, dietary accommodation, sourcing.' },
  { id: 'beverage_intelligence',  name: 'Beverage Program Intel',     weight: 0.09, description: 'Cocktail/wine trends, signatures, attachment rate, program depth.' },
];

// The original 7 core pillars (for backward compat / filtering)
export const CORE_PILLARS = PILLARS.slice(0, 7);

// ── Revenue Leak categories ──
export const REVENUE_LEAKS = [
  { id: 'menu_mispricing',       name: 'Menu Mispricing',           icon: '💰', defaultImpact: 15000 },
  { id: 'low_check_avg',         name: 'Low Check Average',         icon: '📉', defaultImpact: 25000 },
  { id: 'empty_seats',           name: 'Empty Seats / Low Covers', icon: '🪑', defaultImpact: 40000 },
  { id: 'no_events',             name: 'No Events Programming',     icon: '🎤', defaultImpact: 20000 },
  { id: 'no_private_dining',     name: 'No Private Dining Revenue', icon: '🚪', defaultImpact: 30000 },
  { id: 'weak_bev_attachment',   name: 'Weak Beverage Attachment',  icon: '🍸', defaultImpact: 18000 },
  { id: 'poor_upsell',           name: 'Poor Upsell Flow',          icon: '⬆️', defaultImpact: 12000 },
  { id: 'no_merch_retail',       name: 'No Merch / Retail',         icon: '🛍️', defaultImpact: 8000 },
];

// ── Tier thresholds ──
export const TIERS = [
  { name: 'Critical',    min: 0,   max: 3.9, color: '#f87171', label: 'Immediate intervention required' },
  { name: 'At Risk',     min: 4.0, max: 5.9, color: '#fb923c', label: 'Significant gaps to address' },
  { name: 'Developing',  min: 6.0, max: 6.9, color: '#fbbf24', label: 'Foundation in place, needs refinement' },
  { name: 'Performing',  min: 7.0, max: 7.9, color: '#a3e635', label: 'Strong base with upside potential' },
  { name: 'Excelling',   min: 8.0, max: 8.9, color: '#34d399', label: 'Industry-leading in most areas' },
  { name: 'Elite',       min: 9.0, max: 10,  color: '#C9A84C', label: 'Best-in-class hospitality experience' },
];

export function getTier(score) {
  return TIERS.find(t => score >= t.min && score <= t.max) || TIERS[0];
}

// ── Industry benchmark averages (seed data, grows over time) ──
export const INDUSTRY_BENCHMARKS = {
  concept_clarity: 6.2,
  brand_expression: 5.8,
  guest_signal: 6.5,
  operational_confidence: 5.9,
  demand_generation: 5.1,
  commercial_performance: 6.0,
  growth_readiness: 5.3,
  emotional_experience: 5.5,
  menu_intelligence: 5.7,
  beverage_intelligence: 5.4,
};

// ── Score calculations ──

export function calculateNextIndex(scores) {
  let totalWeight = 0;
  let weightedSum = 0;

  PILLARS.forEach((pillar) => {
    const score = scores[pillar.id]?.score;
    if (score != null && score > 0) {
      weightedSum += score * pillar.weight;
      totalWeight += pillar.weight;
    }
  });

  if (totalWeight === 0) return 0;
  return Math.round((weightedSum / totalWeight) * 10) / 10;
}

export function calculateOverallConfidence(scores) {
  const levels = { High: 3, Medium: 2, Low: 1 };
  let sum = 0;
  let count = 0;

  PILLARS.forEach((pillar) => {
    const conf = scores[pillar.id]?.confidence;
    if (conf && levels[conf]) {
      sum += levels[conf];
      count++;
    }
  });

  if (count === 0) return 'Low';
  const avg = sum / count;
  if (avg >= 2.5) return 'High';
  if (avg >= 1.5) return 'Medium';
  return 'Low';
}

// ── "What If" simulator ──
export function simulateImprovement(scores, pillarId, newScore) {
  const simulated = { ...scores };
  simulated[pillarId] = { ...simulated[pillarId], score: newScore };
  return calculateNextIndex(simulated);
}

export function estimateRevenueImpact(currentScore, newScore, annualRevenue = 1000000) {
  const delta = newScore - currentScore;
  // rough model: each 1-point improvement ~ 3-5% revenue lift
  const liftPct = delta * 0.04;
  return Math.round(annualRevenue * liftPct);
}

// ── Memory Test ──
export function getMemoryTestPrompts() {
  return [
    'What would a guest describe to a friend the next day?',
    'Is there a signature dish, drink, or ritual guests would name?',
    'Does the entrance/arrival create a memorable moment?',
    'Is there a visual, sound, or scent that sticks?',
    'Would a guest remember the server\'s warmth or personality?',
  ];
}

// ── Social Proof metrics ──
export const SOCIAL_METRICS = [
  { id: 'posting_consistency', name: 'Posting Consistency', maxScore: 10 },
  { id: 'engagement_rate', name: 'Engagement Rate', maxScore: 10 },
  { id: 'content_quality', name: 'Content Quality', maxScore: 10 },
  { id: 'guest_tagging', name: 'Organic Guest Tagging', maxScore: 10 },
  { id: 'hashtag_performance', name: 'Hashtag Performance', maxScore: 10 },
];

// ── Brand Coherence channels ──
export const BRAND_CHANNELS = [
  { id: 'website', name: 'Website' },
  { id: 'menu', name: 'Menu' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'reviews', name: 'Review Themes' },
  { id: 'physical_space', name: 'Physical Space' },
];

// ── Color/display helpers ──

export function getScoreColor(score) {
  if (score >= 8) return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', hex: '#34d399' };
  if (score >= 6) return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', hex: '#fbbf24' };
  return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', hex: '#f87171' };
}

export function getScoreLabel(score) {
  if (score >= 8) return 'Strong';
  if (score >= 6) return 'Developing';
  return 'Needs Attention';
}

export function createDefaultScores() {
  const scores = {};
  PILLARS.forEach((pillar) => {
    scores[pillar.id] = {
      score: 0,
      rationale: '',
      confidence: 'Medium',
      evidence: 'Inferred',
    };
  });
  return scores;
}

export function createDefaultRevenuLeaks() {
  const leaks = {};
  REVENUE_LEAKS.forEach((leak) => {
    leaks[leak.id] = {
      active: false,
      estimatedImpact: leak.defaultImpact,
      notes: '',
    };
  });
  return leaks;
}

export function createDefaultMemoryTest() {
  return {
    score: 0,
    signatureMoments: ['', '', ''],
    gaps: ['', '', ''],
  };
}

export function createDefaultSocialProof() {
  const social = {};
  SOCIAL_METRICS.forEach((m) => {
    social[m.id] = 5;
  });
  return social;
}

export function createDefaultBrandCoherence() {
  const grid = {};
  BRAND_CHANNELS.forEach((ch) => {
    grid[ch.id] = { aligned: true, notes: '' };
  });
  return grid;
}

export function getConfidenceColor(level) {
  switch (level) {
    case 'High': return 'bg-emerald-500/20 text-emerald-400';
    case 'Medium': return 'bg-amber-500/20 text-amber-400';
    case 'Low': return 'bg-red-500/20 text-red-400';
    default: return 'bg-navy-700 text-navy-300';
  }
}

export function getEvidenceColor(evidence) {
  switch (evidence) {
    case 'Observed': return 'bg-emerald-500/20 text-emerald-400';
    case 'Inferred': return 'bg-amber-500/20 text-amber-400';
    case 'Missing Evidence': return 'bg-red-500/20 text-red-400';
    default: return 'bg-navy-700 text-navy-300';
  }
}
